/**
 * useFeatureGate — Checks if the current user can use a premium feature.
 *
 * Calls the `can_use_premium_feature` Postgres RPC via the repository.
 * Caches per (userId, featureKey) for 5 minutes.
 *
 * Also returns `comingSoon` from the feature registry so the paywall can
 * render "em breve" badges without a separate call.
 *
 * Usage:
 *   const { canUse, comingSoon, gateMode, isLoading } = useFeatureGate('roll_review.create');
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { subscriptionRepository } from '../repositories/supabaseSubscriptionRepository';

export interface FeatureGateResult {
  canUse: boolean;
  comingSoon: boolean;
  gateMode: string | null;
  isLoading: boolean;
  error: string | null;
}

export function useFeatureGate(featureKey: string): FeatureGateResult {
  const { user } = useAuth();

  // Parallel queries: access check + feature metadata (for comingSoon / gateMode)
  const { data: canUse, isLoading: loadingAccess, error: accessError } = useQuery({
    queryKey: ['feature-gate', featureKey, user?.id],
    queryFn: () => subscriptionRepository.canUsePremiumFeature(user!.id, featureKey),
    enabled: !!user,
    staleTime: 5 * 60 * 1_000,
    gcTime:    10 * 60 * 1_000,
  });

  const { data: features, isLoading: loadingFeatures } = useQuery({
    queryKey: ['premium-features'],
    queryFn: () => subscriptionRepository.fetchPremiumFeatures(),
    staleTime: 10 * 60 * 1_000,
    gcTime:    20 * 60 * 1_000,
  });

  const feature = features?.find(f => f.featureKey === featureKey);

  return {
    canUse:      canUse ?? false,
    comingSoon:  feature?.comingSoon ?? false,
    gateMode:    feature?.gateMode ?? null,
    isLoading:   loadingAccess || loadingFeatures,
    error:       accessError instanceof Error ? accessError.message : null,
  };
}

/**
 * useAllFeatureGates — Returns the full feature registry, with access check
 * per feature key resolved via can_use_premium_feature.
 *
 * Used by the paywall to render all benefits at once.
 */
export function useAllFeatureGates(featureKeys: string[]) {
  const { user } = useAuth();

  const { data: features, isLoading } = useQuery({
    queryKey: ['premium-features'],
    queryFn: () => subscriptionRepository.fetchPremiumFeatures(),
    staleTime: 10 * 60 * 1_000,
  });

  return {
    features: features ?? [],
    isLoading,
    getFeature: (key: string) => features?.find(f => f.featureKey === key) ?? null,
  };
}
